import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  IconButton,
  Chip,
  Badge,
  Divider,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  Email as EmailIcon,
  Campaign as CampaignIcon,
  Payment as PaymentIcon,
  NotificationsActive as NotificationIcon,
  Delete as DeleteIcon,
  MarkEmailRead as MarkReadIcon,
  MarkEmailUnread as MarkUnreadIcon,
  Info as InfoIcon
} from '@mui/icons-material';

export default function Inbox({ user }) {
  const [messages, setMessages] = useState([]);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    // Mock messages - replace with Firebase query
    setMessages([
      {
        id: 1,
        type: 'campaign',
        title: 'New Campaign Available',
        message: 'A new gaming highlights campaign has been posted. Budget: $500, Per clip: $25',
        timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000),
        read: false,
        priority: 'high'
      },
      {
        id: 2,
        type: 'submission',
        title: 'Submission Approved',
        message: 'Your clip for "Workout Motivation" campaign has been approved. $30 has been added to your wallet.',
        timestamp: new Date(Date.now() - 5 * 60 * 60 * 1000),
        read: false,
        priority: 'normal'
      },
      {
        id: 3,
        type: 'payment',
        title: 'Payout Processed',
        message: 'Your weekly payout of $150.00 has been sent to your account ending in ****1234',
        timestamp: new Date(Date.now() - 24 * 60 * 60 * 1000),
        read: true,
        priority: 'normal'
      },
      {
        id: 4,
        type: 'system',
        title: 'Platform Update',
        message: 'New features have been added to the platform. Check out the improved submission interface!',
        timestamp: new Date(Date.now() - 48 * 60 * 60 * 1000),
        read: true,
        priority: 'low'
      }
    ]);
  }, [user]);

  const getMessageIcon = (type) => {
    switch (type) {
      case 'campaign':
        return <CampaignIcon color="primary" />;
      case 'submission':
        return <EmailIcon color="success" />;
      case 'payment':
        return <PaymentIcon color="success" />;
      case 'system':
        return <InfoIcon color="info" />;
      default:
        return <NotificationIcon />;
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high':
        return 'error';
      case 'normal':
        return 'warning';
      case 'low':
        return 'default';
      default:
        return 'default';
    }
  };

  const handleMessageClick = (message) => {
    setSelectedMessage(message);
    setDialogOpen(true);
    if (!message.read) {
      markAsRead(message.id);
    }
  };

  const markAsRead = (messageId) => {
    setMessages(prev => 
      prev.map(msg => 
        msg.id === messageId ? { ...msg, read: true } : msg
      )
    );
  };

  const markAsUnread = (messageId) => {
    setMessages(prev => 
      prev.map(msg => 
        msg.id === messageId ? { ...msg, read: false } : msg
      )
    );
  };

  const deleteMessage = (messageId) => {
    setMessages(prev => prev.filter(msg => msg.id !== messageId));
  };

  const filteredMessages = messages.filter(msg => {
    if (filter === 'all') return true;
    if (filter === 'unread') return !msg.read;
    if (filter === 'read') return msg.read;
    return msg.type === filter;
  });

  const unreadCount = messages.filter(msg => !msg.read).length;

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
          Inbox
          {unreadCount > 0 && (
            <Badge badgeContent={unreadCount} color="error" sx={{ ml: 2 }} />
          )}
        </Typography>
        <FormControl sx={{ minWidth: 120 }}>
          <InputLabel>Filter</InputLabel>
          <Select
            value={filter}
            label="Filter"
            onChange={(e) => setFilter(e.target.value)}
            size="small"
          >
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="unread">Unread</MenuItem>
            <MenuItem value="read">Read</MenuItem>
            <MenuItem value="campaign">Campaigns</MenuItem>
            <MenuItem value="submission">Submissions</MenuItem>
            <MenuItem value="payment">Payments</MenuItem>
            <MenuItem value="system">System</MenuItem>
          </Select>
        </FormControl>
      </Box>

      <Paper>
        <List>
          {filteredMessages.length === 0 ? (
            <ListItem>
              <ListItemText
                primary="No messages"
                secondary="You're all caught up!"
              />
            </ListItem>
          ) : (
            filteredMessages.map((message, index) => (
              <React.Fragment key={message.id}>
                <ListItem
                  button
                  onClick={() => handleMessageClick(message)}
                  sx={{
                    bgcolor: message.read ? 'transparent' : 'rgba(255, 82, 82, 0.05)',
                    '&:hover': {
                      bgcolor: message.read ? 'rgba(0, 0, 0, 0.04)' : 'rgba(255, 82, 82, 0.1)'
                    }
                  }}
                >
                  <ListItemIcon>
                    {getMessageIcon(message.type)}
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography
                          variant="subtitle1"
                          sx={{ fontWeight: message.read ? 'normal' : 'bold' }}
                        >
                          {message.title}
                        </Typography>
                        <Chip
                          label={message.priority}
                          size="small"
                          color={getPriorityColor(message.priority)}
                          variant="outlined"
                        />
                      </Box>
                    }
                    secondary={
                      <Box>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                          {message.message.length > 100 
                            ? `${message.message.substring(0, 100)}...`
                            : message.message
                          }
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {message.timestamp.toLocaleString()}
                        </Typography>
                      </Box>
                    }
                  />
                  <ListItemSecondaryAction>
                    <IconButton
                      edge="end"
                      onClick={(e) => {
                        e.stopPropagation();
                        message.read ? markAsUnread(message.id) : markAsRead(message.id);
                      }}
                      sx={{ mr: 1 }}
                    >
                      {message.read ? <MarkUnreadIcon /> : <MarkReadIcon />}
                    </IconButton>
                    <IconButton
                      edge="end"
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteMessage(message.id);
                      }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
                {index < filteredMessages.length - 1 && <Divider />}
              </React.Fragment>
            ))
          )}
        </List>
      </Paper>

      {/* Message Detail Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        {selectedMessage && (
          <>
            <DialogTitle>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {getMessageIcon(selectedMessage.type)}
                <Typography variant="h6">{selectedMessage.title}</Typography>
                <Chip
                  label={selectedMessage.priority}
                  size="small"
                  color={getPriorityColor(selectedMessage.priority)}
                  variant="outlined"
                />
              </Box>
            </DialogTitle>
            <DialogContent>
              <Typography variant="body1" sx={{ mb: 2 }}>
                {selectedMessage.message}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Received: {selectedMessage.timestamp.toLocaleString()}
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDialogOpen(false)}>Close</Button>
              <Button
                onClick={() => {
                  deleteMessage(selectedMessage.id);
                  setDialogOpen(false);
                }}
                color="error"
              >
                Delete
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
}
